import { useRef, useEffect, useMemo } from "react"
import { useFrame } from "@react-three/fiber"
import { useSceneRef } from "../../RefsProvider"
import { Html } from "@react-three/drei"
import { useActsStore } from "../../store/useActsStore"
import { useIsMobile } from "../../utils/useIsMobile"


export default function Controls({ id }) {
  const refOvni = useSceneRef("ovni")
  const refOvniVisual = useSceneRef("ovniVisual")
  const isMobile = useIsMobile()


  const keys = useRef({
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false,
  })

  const tiltRef = useRef({ x: 0, z: 0, vx: 0, vz: 0 })
  const joyRef = useRef({ x: 0, y: 0, active: false })

  const isTouch = useMemo(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(pointer: coarse)").matches
  }, [])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key in keys.current) keys.current[e.key] = true
      if (e.key === "z" || e.key === "w") keys.current.ArrowUp = true
      if (e.key === "q" || e.key === "a") keys.current.ArrowLeft = true
      if (e.key === "s") keys.current.ArrowDown = true
      if (e.key === "d") keys.current.ArrowRight = true
    }

    const handleKeyUp = (e) => {
      if (e.key in keys.current) keys.current[e.key] = false
      if (e.key === "z" || e.key === "w") keys.current.ArrowUp = false
      if (e.key === "q" || e.key === "a") keys.current.ArrowLeft = false
      if (e.key === "s") keys.current.ArrowDown = false
      if (e.key === "d") keys.current.ArrowRight = false
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [])

  useFrame((_, delta) => {
    if (!refOvni.current) return
    delta = Math.min(delta, 1 / 30)

    const body = refOvni.current

    let dx = 0
    let dz = 0

    // --- joystick prioritaire si actif ---
    if (joyRef.current.active) {
      // joy x = droite, joy y = bas (screen)
      dx = -joyRef.current.x
      dz = -joyRef.current.y
    } else {
      if (keys.current.ArrowUp) dz += 1
      if (keys.current.ArrowDown) dz -= 1
      if (keys.current.ArrowLeft) dx += 1
      if (keys.current.ArrowRight) dx -= 1
    }

    // normalisation
    if (dx !== 0 || dz !== 0) {
      const len = Math.hypot(dx, dz)
      if (len > 0) {
        dx /= len
        dz /= len
      }
    }

    const accel = 400
    const maxSpeed = isMobile ? 20 : 60

    if (dx !== 0 || dz !== 0) {
      body.applyImpulse({ x: dx * accel * delta, y: 0, z: dz * accel * delta }, true)
    }

    const v = body.linvel()
    const speedLen = Math.hypot(v.x, v.z)

    if (speedLen > maxSpeed) {
      const k = maxSpeed / speedLen
      body.setLinvel({ x: v.x * k, y: v.y, z: v.z * k }, true)
    }

    // --- tilt ---
    const maxTiltX = 0.3
    const maxTiltZ = 0.3

    const targetTiltX = dz * maxTiltX
    const targetTiltZ = -dx * maxTiltZ

    const k = 50
    const d = 3
    const t = tiltRef.current

    const ax = (targetTiltX - t.x) * k - t.vx * d
    t.vx += ax * delta
    t.x += t.vx * delta

    const az = (targetTiltZ - t.z) * k - t.vz * d
    t.vz += az * delta
    t.z += t.vz * delta

    if (refOvniVisual.current) {
      refOvniVisual.current.rotation.x = t.x
      refOvniVisual.current.rotation.z = t.z
    }
  })

  return (
    <>
      {isTouch && <VirtualJoystick joyRef={joyRef} />}
    </>
  )
}


export function VirtualJoystick({
  joyRef,
  size = 140,
  deadzone = 0.08,
}) {
  const baseRef = useRef(null)
  const stickRef = useRef(null)
  const prevActiveRef = useRef(false)

  const stateRef = useRef({
    active: false,
    pointerId: null,
    cx: 0,
    cy: 0,
    r: 0,
  })


  useEffect(() => {
    const base = baseRef.current
    const stick = stickRef.current
    if (!base || !stick) return

    const setStick = (x, y) => {
      stick.style.transform = `translate(${x}px, ${y}px)`
    }

    const emit = (type, detail) => {
      window.dispatchEvent(new CustomEvent(type, { detail }))
    }

    const compute = (clientX, clientY) => {
      const s = stateRef.current
      const dx = clientX - s.cx
      const dy = clientY - s.cy
      const dist = Math.hypot(dx, dy)
      const clamped = Math.min(dist, s.r)

      const nx = dist > 0 ? dx / dist : 0
      const ny = dist > 0 ? dy / dist : 0

      const px = nx * clamped
      const py = ny * clamped
      setStick(px, py)

      const vx = s.r > 0 ? px / s.r : 0
      const vy = s.r > 0 ? py / s.r : 0

      const len = Math.hypot(vx, vy)
      const isActive = len >= deadzone

      if (!isActive) {
        joyRef.current.x = 0
        joyRef.current.y = 0
        joyRef.current.active = false
      } else {
        joyRef.current.x = vx
        joyRef.current.y = vy
        joyRef.current.active = true
      }

      const wasActive = prevActiveRef.current
      if (!wasActive && joyRef.current.active) emit("joystickdown", { ...joyRef.current })
      if (wasActive && !joyRef.current.active) emit("joystickup", { ...joyRef.current })
      prevActiveRef.current = joyRef.current.active

    }

    const onDown = (e) => {
      base.setPointerCapture(e.pointerId)

      const rect = base.getBoundingClientRect()
      const s = stateRef.current
      s.active = true
      s.pointerId = e.pointerId
      s.cx = rect.left + rect.width / 2
      s.cy = rect.top + rect.height / 2
      s.r = rect.width / 2

      compute(e.clientX, e.clientY)
    }

    const onMove = (e) => {
      const s = stateRef.current
      if (!s.active) return
      if (e.pointerId !== s.pointerId) return
      compute(e.clientX, e.clientY)
    }

    const end = () => {
      const s = stateRef.current
      s.active = false
      s.pointerId = null
      setStick(0, 0)
      joyRef.current.x = 0
      joyRef.current.y = 0
      joyRef.current.active = false
      const wasActive = prevActiveRef.current
      if (wasActive) window.dispatchEvent(new CustomEvent("joystickup", { detail: { ...joyRef.current } }))
      prevActiveRef.current = false
    }

    const onUp = (e) => {
      const s = stateRef.current
      if (e.pointerId !== s.pointerId) return
      end()
    }

    const onCancel = (e) => {
      const s = stateRef.current
      if (e.pointerId !== s.pointerId) return
      end()
    }

    base.addEventListener("pointerdown", onDown)
    base.addEventListener("pointermove", onMove)
    base.addEventListener("pointerup", onUp)
    base.addEventListener("pointercancel", onCancel)

    return () => {
      base.removeEventListener("pointerdown", onDown)
      base.removeEventListener("pointermove", onMove)
      base.removeEventListener("pointerup", onUp)
      base.removeEventListener("pointercancel", onCancel)
    }
  }, [joyRef, deadzone])

  const stickSize = Math.round(size * 0.42)
  const id = useActsStore((s) => s.getCurrentActId())

  return (
    <Html>
      <div
        className={`joystick ${id === 3 ? "is-visible" : "is-hidden"}`}
      >
        <div
          ref={baseRef}
          className="joystick__base"
        >
          <div
            ref={stickRef}
            className="joystick__stick"
          />
        </div>
      </div>
    </Html>
  )

}
